import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';

import * as DefectSignaturesService from '../services/DefectSignaturesService';
import {
    DefectSignature,
    DefectSignaturesFilter,
} from '../types';
import {testingApiAvailable} from '../../testing-api-integration/config';
import {
    notifyError,
    notifySuccess,
} from '../../../components/PlatformNotification';

export const defectSignaturesKeys = {
    all: ['defect-signatures'] as const,
    lists: () => [...defectSignaturesKeys.all, 'list'] as const,
    list: (filters: DefectSignaturesFilter) => [...defectSignaturesKeys.lists(), {filters}] as const,
    details: () => [...defectSignaturesKeys.all, 'detail'] as const,
    detail: (id: string) => [...defectSignaturesKeys.details(), id] as const,
};

const PAGE_SIZE = 50;

export const useDefectSignaturesInfiniteQuery = (filters: DefectSignaturesFilter = {}) => {
    return useInfiniteQuery({
                                queryKey: defectSignaturesKeys.list(filters),
                                queryFn: ({pageParam}: { pageParam: string | null }) =>
                                    DefectSignaturesService.fetchRecords(filters, PAGE_SIZE, pageParam),
                                // The filters are non-key FilterExpressions server-side, so DynamoDB can
                                // hand back a near-empty page with a non-null nextToken. Paging must be
                                // driven by nextToken alone and never stop on an empty page.
                                getNextPageParam: (lastPage) => lastPage?.nextToken ?? undefined,
                                initialPageParam: null as string | null,
                                enabled: testingApiAvailable,
                            });
};

export const useDefectSignatureQuery = (id: string) => {
    return useQuery({
                        queryKey: defectSignaturesKeys.detail(id),
                        queryFn: () => DefectSignaturesService.fetch(id),
                        enabled: testingApiAvailable && !!id,
                    });
};

/**
 * Human triage: move the status and/or attach a Jira key.
 *
 * Invalidates BOTH the detail and every list on success. The lists must go too:
 * a defect filed from the detail page has to stop showing as unfiled when the
 * operator navigates back, and the summary strip counts unfiled defects.
 */
export const useUpdateDefectSignatureMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
                           mutationFn: (input: Pick<DefectSignature, 'id' | 'status'> & { jiraKey?: string | null }) =>
                               DefectSignaturesService.update(input),
                           onSuccess: (_data, variables) => {
                               queryClient.invalidateQueries({queryKey: defectSignaturesKeys.detail(variables.id)});
                               queryClient.invalidateQueries({queryKey: defectSignaturesKeys.lists()});
                           },
                       });
};

/**
 * FNP-969: delete a signature. NOT the triage path and NOT "this defect is fixed"
 * — see services/mutations.ts for the three-verb distinction. This is for clearing
 * an environment, and it discards the occurrence history along with the row.
 *
 * Invalidates `all` rather than just the lists: a deleted signature also backs the
 * detail page and the summary strip's counts, both of which would otherwise keep
 * reporting a defect that no longer exists.
 */
export const useDeleteDefectSignatureMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
                           mutationFn: DefectSignaturesService.deleteRecord,
                           onSuccess: () => {
                               notifySuccess('Defect signature deleted');
                               queryClient.invalidateQueries({queryKey: defectSignaturesKeys.all});
                           },
                           onError: notifyError,
                       });
};
